<template>
  <b-card title="Datenbereinigung (Admin)">
    <b-tabs v-model="currentTab">
      <!-- Stufe 1: Buchungen -->
      <b-tab title="Buchungen löschen">
        <b-alert
          variant="primary"
          show
          class="mt-1"
        >
          <div class="alert-body">
            <feather-icon
              icon="InfoIcon"
              class="mr-50"
            />
            <strong>Stufe 1 – Buchungen kaskadierend löschen.</strong>
            Standort wählen, Buchungen markieren und löschen. Es werden die Buchung und ihre Positionen
            entfernt; Container und Standort bleiben erhalten und werden nur freigegeben. Kritische Fälle
            (unterschriebener Vertrag, aktive Miete ohne Auszugsdatum) werden vor dem Löschen angezeigt und
            müssen bewusst bestätigt werden. Vor jeder Löschung wird ein Snapshot gespeichert (wiederherstellbar).
          </div>
        </b-alert>

        <b-form-group
          label="Standort filtern"
          class="mt-1"
        >
          <b-form-select
            v-model="selectedLocationId"
            :options="locationOptions"
            @change="onLocationChange"
          />
        </b-form-group>

        <b-table
          ref="ordersTable"
          selectable
          select-mode="multi"
          responsive
          striped
          hover
          :items="filteredOrders"
          :fields="orderFields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          show-empty
          empty-text="Keine Buchungen für diesen Standort"
          @row-selected="onOrdersSelected"
        >
          <!-- Auswahl-Spalte: sichtbare Checkbox (Header = alle auswählen) -->
          <template #head(selected)>
            <b-form-checkbox
              :checked="allSelected"
              :indeterminate="someSelected"
              @change="toggleAll"
            />
          </template>
          <!-- Reiner Sicht-Indikator: klick-transparent, Auswahl steuert der Zeilen-Klick -->
          <template #cell(selected)="data">
            <b-form-checkbox
              :checked="data.rowSelected"
              style="pointer-events: none;"
            />
          </template>

          <template #cell(orderDate)="data">
            {{ formatDate(data.value) }}
          </template>

          <template #cell(customer)="data">
            {{ data.value ? `${data.value.firstName} ${data.value.lastName}` : '—' }}
          </template>
        </b-table>

        <b-button
          variant="danger"
          :disabled="selectedOrders.length === 0"
          @click="onValidateOrders"
        >
          {{ selectedOrders.length }} Buchung(en) prüfen & löschen
        </b-button>
      </b-tab>

      <!-- Stufe 2: leeres Inventar -->
      <b-tab title="Leere Container/Standorte löschen">
        <b-alert
          variant="danger"
          show
          class="mt-1"
        >
          <div class="alert-body">
            <feather-icon
              icon="AlertTriangleIcon"
              class="mr-50"
            />
            <strong>Stufe 2 – kompletten Standort samt Containern löschen.</strong>
            Entfernt den gewählten Standort und alle seine Container – aber nur, wenn an keinem Container
            mehr eine Buchung hängt. Sind noch Buchungen vorhanden, wird abgebrochen; dann zuerst im Tab
            „Buchungen löschen" bereinigen. Gelöscht wird kaskadierend (keine verwaisten Container), und vor
            dem Löschen wird ein Snapshot gespeichert.
          </div>
        </b-alert>

        <b-form-group
          label="Standort"
          class="mt-1"
        >
          <b-form-select
            v-model="invLocationId"
            :options="locationOptions"
          />
        </b-form-group>
        <b-button
          variant="danger"
          :disabled="!invLocationId"
          @click="onValidateInventory"
        >
          Standort + leere Container prüfen & löschen
        </b-button>
      </b-tab>

      <!-- Stufe 3: Protokoll & Wiederherstellen -->
      <b-tab title="Protokoll & Wiederherstellen">
        <b-alert
          variant="primary"
          show
          class="mt-1"
        >
          <div class="alert-body">
            <feather-icon
              icon="RotateCcwIcon"
              class="mr-50"
            />
            <strong>Lösch-Protokoll.</strong>
            Jeder Löschvorgang ist hier mit Snapshot protokolliert. Gelöschte <strong>Buchungen</strong>
            lassen sich per Klick wiederherstellen (Original-IDs). Voraussetzung: die IDs sind frei und
            Kunde/Angebot/Container existieren noch — sonst wird mit Hinweis abgebrochen. Bereits
            wiederhergestellte oder Inventar-Einträge sind nicht wiederherstellbar.
          </div>
        </b-alert>

        <b-table
          responsive
          striped
          :items="deletionLogs"
          :fields="logFields"
          show-empty
          empty-text="Noch keine Löschvorgänge protokolliert"
          class="mt-1"
        >
          <template #cell(created_at)="data">
            {{ formatDate(data.value) }}
          </template>
          <template #cell(ref)="data">
            {{ logRef(data.item) }}
          </template>
          <template #cell(customer)="data">
            {{ logCustomer(data.item) }}
          </template>
          <template #cell(affected)="data">
            {{ summarizeAffected(data.item.affectedIds) }}
          </template>
          <template #cell(status)="data">
            <b-badge :variant="data.item.restoredAt ? 'success' : 'secondary'">
              {{ data.item.restoredAt ? `wiederhergestellt ${formatDate(data.item.restoredAt)}` : 'offen' }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="data.item.entityType !== 'order' || !!data.item.restoredAt"
              @click="onRestore(data.item)"
            >
              Wiederherstellen
            </b-button>
          </template>
        </b-table>
      </b-tab>
    </b-tabs>
  </b-card>
</template>

<script>
import {
  BCard,
  BTabs,
  BTab,
  BTable,
  BButton,
  BFormGroup,
  BFormSelect,
  BFormCheckbox,
  BAlert,
  BBadge,
} from 'bootstrap-vue'
import { canExecute } from '@/service/dataCleanup.service'

export default {
  components: {
    BCard,
    BTabs,
    BTab,
    BTable,
    BButton,
    BFormGroup,
    BFormSelect,
    BFormCheckbox,
    BAlert,
    BBadge,
  },
  data() {
    return {
      currentTab: 0,
      selectedLocationId: null,
      invLocationId: null,
      selectedOrders: [],
      sortBy: 'orderDate',
      sortDesc: true,
      orderFields: [
        { key: 'selected', label: '', sortable: false },
        { key: 'id', label: 'ID', sortable: true },
        { key: 'orderDate', label: 'Buchungsdatum', sortable: true },
        { key: 'customer', label: 'Kunde', sortable: false },
      ],
      logFields: [
        { key: 'created_at', label: 'Zeitpunkt' },
        { key: 'ref', label: 'Objekt' },
        { key: 'customer', label: 'Kunde' },
        { key: 'affected', label: 'Umfang' },
        { key: 'actorEmail', label: 'Gelöscht von' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Aktion' },
      ],
    }
  },
  computed: {
    locationOptions() {
      const locs = this.$store.getters.locationsEntries || []
      return [
        { value: null, text: 'Bitte wählen' },
        ...locs.map(l => ({ value: l.id, text: l.name })),
      ]
    },
    orderEntries() {
      return this.$store.getters.orderEntries || []
    },
    deletionLogs() {
      return this.$store.getters.deletionLogs || []
    },
    allSelected() {
      return this.filteredOrders.length > 0 && this.selectedOrders.length === this.filteredOrders.length
    },
    someSelected() {
      return this.selectedOrders.length > 0 && !this.allSelected
    },
    filteredOrders() {
      if (!this.selectedLocationId) return []
      // GET /orders only returns order_items with `storage` as an ID (one-level populate),
      // so resolve the storage (which carries its location) via the storages store —
      // same pattern as OrdersDetail.vue's storageById(item.storage).
      return this.orderEntries.filter(o => (o.order_items || []).some(it => {
        const storageId = it.storage_id || (it.storage && it.storage.id) || it.storage
        const storage = this.$store.getters.storageById(storageId)
        const loc = storage && storage.location
        return loc && loc.id === this.selectedLocationId
      }))
    },
  },
  created() {
    this.$store.dispatch('getLocationsEntries')
    this.$store.dispatch('getOrderEntries')
    this.$store.dispatch('getStorageEntries')
    this.$store.dispatch('fetchDeletionLogs')
  },
  methods: {
    onLocationChange() {
      this.selectedOrders = []
      if (this.$refs.ordersTable) this.$refs.ordersTable.clearSelected()
    },
    onOrdersSelected(rows) {
      this.selectedOrders = rows
    },
    toggleAll(checked) {
      if (checked) this.$refs.ordersTable.selectAllRows()
      else this.$refs.ordersTable.clearSelected()
    },
    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    },
    async onValidateOrders() {
      const ids = this.selectedOrders.map(o => o.id)
      const data = await this.$store.dispatch('validateOrders', ids)
      const warnings = (data.preview || []).flatMap(p => p.warnings || [])
      await this.confirmAndDeleteOrders(ids, warnings)
    },
    async confirmAndDeleteOrders(ids, warnings) {
      const warnText = warnings.length
        ? `\n\n⚠️ ${warnings.length} kritische Hinweise:\n${warnings.map(w => `• ${w.detail}`).join('\n')}`
        : ''
      const result = await this.$swal({
        title: `${ids.length} Buchung(en) endgültig löschen?`,
        text: `Vor dem Löschen wird ein Snapshot gespeichert.${warnText}`,
        icon: warnings.length ? 'warning' : 'question',
        showCancelButton: true,
        confirmButtonText: warnings.length ? 'Trotz Hinweisen löschen' : 'Ja, löschen!',
        cancelButtonText: 'Abbrechen',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-primary ml-1',
        },
        buttonsStyling: false,
      })
      // Abbruch (Cancel/Backdrop) immer respektieren — unabhängig von Warnungen.
      if (result.value !== true) return
      const acknowledged = result.value === true
      if (!canExecute({ warnings, acknowledged })) return
      const res = await this.$store.dispatch('executeOrderDeletion', {
        ids,
        warningsAcknowledged: acknowledged,
      })
      const deleted = (res.results || []).filter(r => r.status === 'deleted').length
      this.$swal({
        title: 'Fertig',
        text: `${deleted} Buchung(en) gelöscht.`,
        icon: 'success',
        buttonsStyling: false,
        customClass: { confirmButton: 'btn btn-primary' },
      })
      this.$store.dispatch('getOrderEntries')
      this.selectedOrders = []
    },
    async onValidateInventory() {
      const data = await this.$store.dispatch('validateInventory', {
        storageIds: [],
        locationIds: [this.invLocationId],
      })
      const entry = (data.preview || [])[0] || { deletable: false, reason: 'Unbekannt' }
      if (!entry.deletable) {
        this.$swal({
          title: 'Löschen nicht möglich',
          text: entry.reason,
          icon: 'error',
          buttonsStyling: false,
          customClass: { confirmButton: 'btn btn-primary' },
        })
        return
      }
      const result = await this.$swal({
        title: 'Standort + leere Container löschen?',
        text: 'Es werden nur leere Container und der leere Standort entfernt.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ja, löschen!',
        cancelButtonText: 'Abbrechen',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-primary ml-1',
        },
        buttonsStyling: false,
      })
      if (result.value !== true) return
      await this.$store.dispatch('executeInventoryDeletion', {
        storageIds: [],
        locationIds: [this.invLocationId],
      })
      this.$swal({
        title: 'Fertig',
        text: 'Standort gelöscht.',
        icon: 'success',
        buttonsStyling: false,
        customClass: { confirmButton: 'btn btn-primary' },
      })
      this.$store.dispatch('getLocationsEntries')
      this.invLocationId = null
    },
    logRef(log) {
      if (log.entityType === 'order') {
        const oid = (log.snapshot && log.snapshot.orderId)
          || (Array.isArray(log.affectedIds) && (log.affectedIds.find(a => a.model === 'order') || {}).id)
        return oid ? `Buchung #${oid}` : 'Buchung'
      }
      const loc = log.snapshot && (log.snapshot.name || log.snapshot.shortName)
      return loc ? `Standort ${loc}` : 'Inventar'
    },
    logCustomer(log) {
      const c = log.snapshot && log.snapshot.order && log.snapshot.order.customer
      if (!c) return '—'
      if (typeof c === 'object') {
        const name = `${c.firstName || ''} ${c.lastName || ''}`.trim()
        return name || (c.id ? `#${c.id}` : '—')
      }
      return `#${c}`
    },
    summarizeAffected(affectedIds) {
      if (!Array.isArray(affectedIds) || !affectedIds.length) return '—'
      const labels = {
        order: 'Buchung',
        'order-item': 'Position',
        storage: 'Container',
        location: 'Standort',
        'storage-status': 'Status',
        'zoho-contract': 'Vertrag',
      }
      const counts = {}
      affectedIds.forEach(a => {
        const key = a.model || a.type || 'sonstige'
        counts[key] = (counts[key] || 0) + 1
      })
      return Object.keys(counts).map(k => `${counts[k]}× ${labels[k] || k}`).join(', ')
    },
    async onRestore(logEntry) {
      const result = await this.$swal({
        title: 'Buchung wiederherstellen?',
        text: 'Die Buchung und ihre Positionen werden aus dem Snapshot mit ihren Original-IDs wieder angelegt.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ja, wiederherstellen',
        cancelButtonText: 'Abbrechen',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (result.value !== true) return
      const res = await this.$store.dispatch('restoreOrder', logEntry.id)
      if (res.status === 'restored') {
        this.$swal({
          title: 'Wiederhergestellt',
          text: `Buchung ${res.orderId} ist zurück.`,
          icon: 'success',
          buttonsStyling: false,
          customClass: { confirmButton: 'btn btn-primary' },
        })
      } else {
        this.$swal({
          title: 'Nicht möglich',
          text: (res.reasons || ['Unbekannter Grund']).join('\n'),
          icon: 'error',
          buttonsStyling: false,
          customClass: { confirmButton: 'btn btn-primary' },
        })
      }
      this.$store.dispatch('fetchDeletionLogs')
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';
</style>
